import PropTypes from 'prop-types';
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import classNames from 'classnames';
import { Button } from '@gouvfr-lasuite/cunningham-react';
import { Badge, Icon } from '@gouvfr-lasuite/ui-kit';
import { upperFirst, camelCase } from 'lodash';
import styles from '../Board.module.scss';
import SortableCard from '../Card/SortableCard';
import SubtaskGroup from './SubtaskGroup'; // ─── 5b ───
import ListDropTarget from './ListDropTarget';
import ListNameEdit from './ListNameEdit';
import usePopup from '../../../lib/popup/use-popup';
import ListActionsStep from '../../../steps/ListActionsStep/ListActionsStep';
import globalStyles from '../../../assets/styles/styles.module.scss';

function List({
  id,
  name,
  isPersisted,
  color,
  cardIds,
  canEdit,
  isOwner,
  listGroups, // ─── 5c ───
  parentNames, // ─── 5c ───
  hideHeader, // ─── C2 : masquer le header dans le couloir feuilles ───
  onUpdate,
  onDelete,
  onSort,
}) {
  const [t] = useTranslation();

  // this is filled by SortableCard on mount (Map<cardId, SortableInstance>)
  const cardSortablesRef = useRef(new Map());

  const handleSortableReady = useCallback((cardId, sortableInstance) => {
    if (sortableInstance) {
      cardSortablesRef.current.set(cardId, sortableInstance);
    } else {
      cardSortablesRef.current.delete(cardId);
    }
  }, []);

  // patching sortable indexes directly will prevent rerendering a lot of time `SortableCard`
  // when moving a card and due to subsequent position changes on other cards, this was putting pressure on the `DragOverlay`
  // unable to hide its overlay because optimistic UI had the priority (triggered from the redux action)
  useLayoutEffect(() => {
    cardIds.forEach((cardId, cardIndex) => {
      const proxied = cardSortablesRef.current.get(cardId);

      if (proxied) {
        proxied.draggable.sortable.index = cardIndex;
      }
    });
  }, [cardIds]);

  const [isListActionsPopoverOpen, setIsListActionsPopoverOpen] = useState(false);

  const nameEdit = useRef(null);

  const ListActionsPopover = usePopup(ListActionsStep);

  const handleHeaderClick = useCallback(() => {
    if (isPersisted && isOwner) {
      nameEdit.current.open();
    }
  }, [isPersisted, isOwner]);

  const handleHeaderKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleHeaderClick();
      }
    },
    [handleHeaderClick],
  );

  const handleNameUpdate = useCallback(
    (newName) => {
      onUpdate({
        name: newName,
      });
    },
    [onUpdate],
  );

  const handleColorEdit = useCallback(
    (newColor) => {
      onUpdate({
        color: newColor,
      });
    },
    [onUpdate],
  );

  const handleNameEdit = useCallback(() => {
    nameEdit.current.open();
  }, []);

  // ─── 5c : construction du rendu ordonné (orphelins + encadrés) ───
  // On parcourt cardIds DANS L'ORDRE (positions) pour préserver la cohérence DnD.
  // Un encadré est rendu UNE SEULE FOIS, à la position de sa première carte enfant.
  const { parentGroups, orphanCardIds } = listGroups;

  // Normalisation défensive des types (ids string vs number, cf. §13.2)
  const orphanSet = new Set(orphanCardIds.map(String));

  // Map cardId (string) → parentId (string), pour savoir à quel groupe appartient une carte
  const parentIdByCardId = new Map();
  Object.keys(parentGroups).forEach((parentId) => {
    parentGroups[parentId].forEach((childId) => {
      parentIdByCardId.set(String(childId), String(parentId));
    });
  });

  const renderedGroups = new Set(); // encadrés déjà rendus

  const renderCardsContent = () =>
    cardIds.map((cardId) => {
      const key = String(cardId);

      // Carte orpheline → rendu plat
      if (orphanSet.has(key)) {
        return (
          <SortableCard
            key={cardId}
            id={cardId}
            listId={id}
            canEdit={canEdit}
            onSortableReady={handleSortableReady}
          />
        );
      }

      // Carte appartenant à un groupe
      const parentId = parentIdByCardId.get(key);
      if (parentId !== undefined) {
        // Rendre l'encadré une seule fois (à la première carte rencontrée du groupe)
        if (renderedGroups.has(parentId)) {
          return null;
        }
        renderedGroups.add(parentId);

        return (
          <SubtaskGroup
            key={`group-${parentId}`}
            parentId={parentId}
            parentName={parentNames[parentId]}
            childIds={parentGroups[parentId]}
            listId={id}
            canEdit={canEdit}
            onSortableReady={handleSortableReady}
          />
        );
      }

      // Sécurité : carte non classée (ne devrait pas arriver) → rendu plat
      return (
        <SortableCard
          key={cardId}
          id={cardId}
          listId={id}
          canEdit={canEdit}
          onSortableReady={handleSortableReady}
        />
      );
    });

  return (
    <>
      {!hideHeader && (
        <div
          className={classNames(
            styles.listHeader,
            isOwner && styles.listHeaderEditable,
            isListActionsPopoverOpen ? styles.popoverOpened : '',
          )}
        >
          <div
            onClick={handleHeaderClick}
            onKeyDown={handleHeaderKeyDown}
            role="button"
            className={styles.listHeaderNameEdit}
            tabIndex={isOwner ? 0 : -1}
          >
            <ListNameEdit ref={nameEdit} defaultValue={name} onUpdate={handleNameUpdate}>
              <div className={styles.listHeaderContent}>
                {color && (
                  <Icon
                    name="circle"
                    className={classNames(
                      styles.listColor,
                      globalStyles[`color${upperFirst(camelCase(color))}`],
                    )}
                  />
                )}
                <p className={styles.listName}>{name}</p>
                <Badge type="neutral">{cardIds.length}</Badge>
              </div>
            </ListNameEdit>
          </div>

          {isPersisted && isOwner && (
            <ListActionsPopover
              onNameEdit={handleNameEdit}
              onDelete={onDelete}
              onSort={onSort}
              color={color}
              onColorEdit={handleColorEdit}
              onOpenChange={setIsListActionsPopoverOpen}
            >
              <Button className={styles.listHeaderButton} color="brand" variant="tertiary">
                <Icon type="outlined" name="more_horiz" />
              </Button>
            </ListActionsPopover>
          )}
        </div>
      )}
      <div className={classNames(styles.cardsContainer)}>
        {renderCardsContent()}
        <ListDropTarget listId={id} index={cardIds.length} />
      </div>
    </>
  );
}

List.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  isPersisted: PropTypes.bool.isRequired,
  color: PropTypes.string.isRequired,
  cardIds: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  canEdit: PropTypes.bool.isRequired,
  isOwner: PropTypes.bool.isRequired,
  listGroups: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  parentNames: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  hideHeader: PropTypes.bool, // ─── C2 ───
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSort: PropTypes.func.isRequired,
};

List.defaultProps = {
  hideHeader: false, // ─── C2 ───
};

export default React.memo(List);
